import { getWidgetStores, InfoCard } from '@/entities/widget';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import Origin from '@/shared/assets/images/origin.svg';
import { OptimizedImage } from '@/shared/ui';
import {
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ChartColumnIcon, InfoIcon, LightbulbIcon } from 'lucide-react';
import { DetailStack } from '../DetailStack/DetailStack';
import type { OpportunitySectionProps } from './OpportunitySection.types';
import { useTranslation } from 'react-i18next';

export function OpportunitySection({ item }: OpportunitySectionProps) {
  const { t } = useTranslation('contentTest');

  // ChatMarkdownRenderer via the entities/widget binding seam.
  const { ChatMarkdownRenderer } = getWidgetStores().chatStore;
  const opportunityHint = t(
    'socialBriefWidget.opportunitySection.overviewOfTheSelectedOpportunity',
  );
  return (
    <div data-testid="social-brief-opportunity-section" className="flex flex-col gap-5">
      <Separator />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ChartColumnIcon className="size-5 text-muted-foreground" />
          <span className="text-body-lg font-medium text-foreground">
            {t('socialBriefWidget.opportunitySection.opportunity')}
          </span>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                aria-label={opportunityHint}
                className="flex items-center text-muted-foreground"
              >
                <InfoIcon className="size-5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="left">{opportunityHint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex flex-col gap-5 px-6">
        <InfoCard style="bg-transparent border border-border-light w-full">
          <div className="flex flex-col gap-3">
            <span className="text-body-lg font-medium text-muted-foreground">
              {t('socialBriefWidget.opportunitySection.trend')}
            </span>
            <span className="text-body-lg font-medium text-foreground">
              {item.overview.trendSummary.title}
            </span>
            <div className="text-body-lg text-foreground">
              <ChatMarkdownRenderer
                content={item.overview.trendSummary.description}
                disableAnimation
                inline
              />
            </div>
          </div>
        </InfoCard>

        <InfoCard style="bg-card border border-border-light w-full">
          <div className="flex items-center gap-2">
            <OptimizedImage
              src={Origin}
              width={24}
              height={24}
              alt={t('socialBriefWidget.opportunitySection.widgetIcon')}
              priority
            />
            <span className="text-body-lg font-medium text-foreground">
              {t('socialBriefWidget.opportunitySection.trendOrigin')}
            </span>
          </div>

          {Object.values(item.overview.trendOrigin.sources)
            .filter((source) => source.post)
            .map((source) => {
              if (!source.post) return null;
              return (
                <InfoCard
                  key={source.key}
                  link={source.url}
                  style="bg-transparent border border-border-light w-full mt-4 p-6"
                >
                  <DetailStack>
                    <span className="text-body-md font-medium text-foreground">
                      {source.post.published_by_name}
                    </span>
                    <span className="text-body-sm text-muted-foreground">
                      {source.post.published_by_id}
                    </span>
                    <span className="text-body-md text-foreground">
                      {source.post.description || source.post.postAnalysis?.summary}
                    </span>
                    <div className="flex justify-between text-body-sm text-muted-foreground">
                      <span>{source.post.source}</span>
                      <span>{formatRelativeDate(source.post.published_at)}</span>
                    </div>
                  </DetailStack>
                </InfoCard>
              );
            })}
          {/* Brand purple: the active theme carries no semantic token for it, and this
              callout also renders into the exported brief. */}
          <div className="mt-6 flex flex-col gap-2 rounded-2xl bg-[#F2EEFF] p-6">
            <DetailStack>
              <div className="flex items-center gap-2">
                <LightbulbIcon className="size-5 text-[#4F24EE]" />
                <span className="text-body-md font-medium text-[#4F24EE]">
                  {t('socialBriefWidget.opportunitySection.trendInsight')}
                </span>
              </div>
              <div className="text-body-md text-foreground">
                <ChatMarkdownRenderer
                  content={item.overview.trendOrigin.trendInsight}
                  disableAnimation
                  inline
                />
              </div>
            </DetailStack>
          </div>
        </InfoCard>
      </div>
    </div>
  );
}
